// Utilities
import { defineStore } from 'pinia'
import type { ErpType } from '@/constants/erpTypes'

/** One society of a consolidated company. `country` is ISO-2 and gates country-specific fields per DESTINATION society. */
export interface CompanySociety {
  id: number,
  name: string,
  country?: string,
  conditions?: string,
  observations?: string,
}

interface Society {
  id: number,
  name: string,
  plan: string,
  paymentStatus: string,
  hasAi: boolean,
  hasBudget: boolean,
  hasLineRelations: boolean,
  hasTransactions: boolean,
  hasErpConnection: boolean,
  hasAdvancedQuotations: boolean,
  country: string,
  timezone: string,
  erp: ErpType | "",
  isConsolidated: boolean,
  companyId: number,
  /** Language this society writes its configurable content in (never how anyone reads the app). */
  contentLanguage: string,
  /** Languages the company works in. */
  enabledLanguages: string[],
  companySocieties: CompanySociety[],
  firewallEnabled: boolean,
  // Derived by the backend from RiskProviderConfig.enabled matched by the society's
  // country. Gates the EXTERNAL risk dimension in the UI. Single source of truth is
  // the provider config, not a manually-granted permission.
  hasExternalRisk: boolean,
  // Same mould as hasExternalRisk: booleans derived by the backend, not permissions.
  // `fintaxAvailable` = the society's country has a FinnTax engine (today only CO) and
  // gates the configuration + masters surfaces; `fintaxEnabled` = available AND the
  // society switch is on, and gates the operational surfaces (proposal card, node).
  fintaxAvailable: boolean,
  fintaxEnabled: boolean,
  /** Configuración > Pagos: manual payments are created one per cost center. */
  splitManualPaymentsByCostCenter: boolean,
  permissions: string[],
}

export const useSocietyStore = defineStore('society', {
  state: (): Society => ({
    id: 0,
    name: "",
    plan: "",
    paymentStatus: "",
    hasAi: false,
    hasBudget: false,
    hasLineRelations: false,
    hasTransactions: false,
    hasErpConnection: false,
    hasAdvancedQuotations: false,
    country: "",
    timezone: "",
    erp: "",
    isConsolidated: false,
    companyId: 0,
    contentLanguage: "es",
    enabledLanguages: [],
    companySocieties: [],
    firewallEnabled: false,
    hasExternalRisk: false,
    fintaxAvailable: false,
    fintaxEnabled: false,
    splitManualPaymentsByCostCenter: false,
    permissions: [],
  }),
  getters: {
    getId: (state) => state.id,
    getName: (state) => state.name,
    getPlan: (state) => state.plan,
    getPaymentStatus: (state) => state.paymentStatus,
    getHasAi: (state) => state.hasAi,
    getHasBudget: (state) => state.hasBudget,
    getHasLineRelations: (state) => state.hasLineRelations,
    getHasTransactions: (state) => state.hasTransactions,
    getHasErpConnection: (state) => state.hasErpConnection,
    getHasAdvancedQuotations: (state) => state.hasAdvancedQuotations,
    getCountry: (state) => state.country,
    getErp: (state) => state.erp,
    getIsConsolidated: (state) => state.isConsolidated,
    getCompanyId: (state) => state.companyId,
    getCompanySocieties: (state) => state.companySocieties,
    // why: one rule for every surface of the request group: a consolidated company with a single society has nobody to split with.
    canGroupRequests: (state) => state.isConsolidated && state.companySocieties.length > 1,
    getFirewallEnabled: (state) => state.firewallEnabled,
    // Mirror of Society.has_permission(name) in finnecto-api. Reads the raw
    // `permissions: string[]` array from the backend tenant config; returns true
    // if the named permission is attached to the current society.
    hasPermission: (state) => (name: string): boolean => state.permissions.includes(name),
  },
  actions: {
    setSociety(society: Society) {
      this.id = society.id
      this.name = society.name
      this.plan = society.plan
      this.paymentStatus = society.paymentStatus
      this.hasAi = society.hasAi
      this.hasBudget = society.hasBudget
      this.hasLineRelations = society.hasLineRelations
      this.hasTransactions = society.hasTransactions
      this.hasErpConnection = society.hasErpConnection
      this.hasAdvancedQuotations = society.hasAdvancedQuotations
      this.country = society.country
      this.timezone = society.timezone
      this.erp = society.erp
      this.isConsolidated = society.isConsolidated
      this.companyId = society.companyId
      this.contentLanguage = society.contentLanguage ?? "es"
      this.enabledLanguages = society.enabledLanguages ?? []
      this.companySocieties = society.companySocieties
      this.firewallEnabled = society.firewallEnabled
      this.hasExternalRisk = society.hasExternalRisk
      this.fintaxAvailable = society.fintaxAvailable
      this.fintaxEnabled = society.fintaxEnabled
      this.splitManualPaymentsByCostCenter = society.splitManualPaymentsByCostCenter ?? false
      this.permissions = society.permissions ?? []
    },
    setId(id: number) {
      this.id = id
    },
    setName(name: string) {
      this.name = name
    },
    setPlan(plan: string) {
      this.plan = plan
    },
    setPaymentStatus(paymentStatus: string) {
      this.paymentStatus = paymentStatus
    },
    setHasAi(hasAi: boolean) {
      this.hasAi = hasAi
    },
    setHasBudget(hasBudget: boolean) {
      this.hasBudget = hasBudget
    },
    setHasLineRelations(hasLineRelations: boolean) {
      this.hasLineRelations = hasLineRelations
    },
    setHasTransactions(hasTransactions: boolean) {
      this.hasTransactions = hasTransactions
    },
    setHasErpConnection(hasErpConnection: boolean) {
      this.hasErpConnection = hasErpConnection
    },
    setHasAdvancedQuotations(hasAdvancedQuotations: boolean) {
      this.hasAdvancedQuotations = hasAdvancedQuotations
    },
    setCountry(country: string) {
      this.country = country;
    },
    setTimezone(timezone: string) {
      this.timezone = timezone;
    },
    setErp(erp: ErpType | "") {
      this.erp = erp
    },
    setIsConsolidated(isConsolidated: boolean) {
      this.isConsolidated = isConsolidated
    },
    setCompanyId(companyId: number) {
      this.companyId = companyId
    },
    setContentLanguage(contentLanguage: string) {
      this.contentLanguage = contentLanguage
    },
    setEnabledLanguages(enabledLanguages: string[]) {
      this.enabledLanguages = enabledLanguages
    },
    setCompanySocieties(companySocieties: CompanySociety[]) {
      this.companySocieties = companySocieties
    },
    setFirewallEnabled(firewallEnabled: boolean) {
      this.firewallEnabled = firewallEnabled
    },
    setPermissions(permissions: string[]) {
      this.permissions = permissions
    },
    clearSociety() {
      this.id = 0
      this.name = ""
      this.plan = ""
      this.paymentStatus = ""
      this.hasAi = false
      this.hasBudget = false
      this.hasLineRelations = false
      this.hasTransactions = false
      this.hasErpConnection = false
      this.hasAdvancedQuotations = false
      this.country = ""
      this.timezone = ""
      this.erp = ""
      this.isConsolidated = false
      this.companyId = 0
      this.contentLanguage = "es"
      this.enabledLanguages = []
      this.companySocieties = []
      this.firewallEnabled = false
      this.hasExternalRisk = false
      this.fintaxAvailable = false
      this.fintaxEnabled = false
      this.splitManualPaymentsByCostCenter = false
      this.permissions = []
    },
  },
  persist: true
})
